import { BaseApiService } from '../base/BaseApiService';
import {
  CreateICcIdRequest,
  UpdateICcIdRequest,
  ICcIdResponse,
  ICcIdListResponse,
  ICcIdStockResponse,
  ICcIdStockSummaryResponse,
  ICcIdAvailabilityResponse,
  ICcIdAllocationFlagResponse,
  ICcIdAvailableCountResponse,
  ICcIdBatchCreateRequest,
  ICcIdExcelResultResponse,
  FastchipExcelCancellationResultResponse,
  ICcIdFilters,
  ICcIdBatchActionRequest,
  ICcIdBatchResultResponse,
  BatchUpdateICcIdsRequest,
  BatchUpdateICcIdsResult,
  FastChipMovementEventDto,
  FastChipMovementHistoryResponse
} from '../../types/dtos';
import { ApiResponse, PaginatedResponse } from '../../types/api';

export class ICcIdService extends BaseApiService {
  private readonly basePath = '/icCids';

  // ===== MÉTODOS BÁSICOS =====

  /**
   * Retorna todos os ICCIDs cadastrados
   */
  async getAllICcIds(): Promise<ICcIdResponse[]> {
    const response = await this.api.get<ApiResponse<ICcIdResponse[]>>(this.basePath);
    return this.extractData(response);
  }

  /**
   * Retorna um ICCID específico por ID
   */
  async getICcIdById(id: string): Promise<ICcIdResponse> {
    const response = await this.api.get<ApiResponse<ICcIdResponse>>(`${this.basePath}/${id}`);
    return this.extractData(response);
  }

  /**
   * Retorna um ICCID específico por CCID
   */
  async getICcIdByCcId(ccId: string): Promise<ICcIdResponse> {
    const response = await this.api.get<ApiResponse<ICcIdResponse>>(`${this.basePath}/by-ccid/${ccId}`);
    return this.extractData(response);
  }

  /**
   * Busca ICCIDs com filtros e paginação
   */
  async searchICcIds(
    filters: ICcIdFilters = {},
    page: number = 1,
    pageSize: number = 10,
    orderBy?: string,
    descending: boolean = false,
    request?: { signal?: AbortSignal; compact?: boolean }
  ): Promise<PaginatedResponse<ICcIdResponse>> {
    const params = new URLSearchParams();
    
    // Adicionar filtros
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (value instanceof Date) {
          params.append(key, value.toISOString());
        } else if (typeof value === 'boolean') {
          // Booleanos devem ser enviados como "true" ou "false" (padrão do ASP.NET)
          params.append(key, value.toString());
        } else {
          params.append(key, String(value));
        }
      }
    });

    // Adicionar parâmetros de paginação
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    
    if (orderBy) {
      params.append('orderBy', orderBy);
    }
    
    if (descending) {
      params.append('descending', 'true');
    }

    if (request?.compact) {
      params.append('compact', 'true');
    }

    // A API agora retorna um objeto paginado completo ao invés de usar headers
    const response = await this.api.get<ApiResponse<PaginatedResponse<ICcIdResponse>>>(
      `${this.basePath}/search?${params.toString()}`,
      { signal: request?.signal }
    );

    return this.extractData(response);
  }

  /**
   * Cria um novo ICCID
   */
  async createICcId(request: CreateICcIdRequest): Promise<ICcIdResponse> {
    const response = await this.api.post<ApiResponse<ICcIdResponse>>(this.basePath, request);
    return this.extractData(response);
  }

  /**
   * Cria múltiplos ICCIDs em lote
   */
  async createICcIdsBatch(request: ICcIdBatchCreateRequest): Promise<ICcIdResponse[]> {
    const response = await this.api.post<ApiResponse<ICcIdResponse[]>>(`${this.basePath}/batch`, request);
    return this.extractData(response);
  }

  /**
   * Cria ICCIDs a partir de arquivo Excel
   * @param file Arquivo Excel com os ICCIDs
   * @param estados String com estados separados por vírgula (ex: "SP,RJ,MG")
   * @param clusterParana Opcional. Quando PR está em estados: ER ou MH (cluster EAS/TIM). Pode ser omitido se a coluna CLUSTER da planilha estiver preenchida.
   */
  async createICcIdsFromExcel(
    file: File,
    estados: string,
    clusterParana?: string | null
  ): Promise<ICcIdExcelResultResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('estados', estados);
    if (clusterParana) {
      formData.append('clusterParana', clusterParana);
    }

    // false remove o default application/json e deixa o browser definir multipart + boundary.
    const response = await this.api.post<ApiResponse<ICcIdExcelResultResponse>>(
      `${this.basePath}/excel`,
      formData,
      { headers: { 'Content-Type': false as unknown as string } }
    );
    return this.extractData(response);
  }

  /**
   * Processa Excel de retorno da automação FastChip e solicita cancelamentos elegíveis
   */
  async uploadFastchipExcel(file: File): Promise<FastchipExcelCancellationResultResponse> {
    const formData = new FormData();
    formData.append('file', file);

    // false remove o default application/json e deixa o browser definir multipart + boundary.
    // Timeout alto: Fase 2 consulta VTME + cancelamentos/liberações podem passar de 30s.
    const response = await this.api.post<ApiResponse<FastchipExcelCancellationResultResponse>>(
      '/v1/contracts/fastchip/import',
      formData,
      {
        headers: { 'Content-Type': false as unknown as string },
        timeout: 300_000,
      }
    );

    return this.extractData(response);
  }

  /**
   * Atualiza um ICCID existente
   */
  async updateICcId(id: string, request: UpdateICcIdRequest): Promise<ICcIdResponse> {
    const response = await this.api.put<ApiResponse<ICcIdResponse>>(`${this.basePath}/${id}`, request);
    return this.extractData(response);
  }

  /**
   * Remove um ICCID permanentemente
   */
  async deleteICcId(id: string): Promise<void> {
    await this.api.delete(`${this.basePath}/${id}`);
  }

  /**
   * Remove um ICCID logicamente (soft delete)
   */
  async softDeleteICcId(id: string): Promise<void> {
    await this.api.delete(`${this.basePath}/${id}/soft`);
  }

  // ===== MÉTODOS DE CONTROLE DE DISPONIBILIDADE =====

  /**
   * Disponibiliza um ICCID (torna disponível)
   */
  async makeICcIdAvailable(id: string): Promise<ICcIdResponse> {
    const response = await this.api.patch<ApiResponse<ICcIdResponse>>(`${this.basePath}/${id}/make-available`);
    return this.extractData(response);
  }

  /**
   * Indisponibiliza um ICCID (torna indisponível)
   */
  async makeICcIdUnavailable(id: string, motivo?: string): Promise<ICcIdResponse> {
    const response = await this.api.patch<ApiResponse<ICcIdResponse>>(
      `${this.basePath}/${id}/make-unavailable`,
      motivo ? { motivoIndisponibilidade: motivo } : {}
    );
    return this.extractData(response);
  }

  // ===== MÉTODOS DE ASSOCIAÇÃO COM CONTRATOS =====

  /**
   * Associa um ICCID a um contrato
   */
  async assignICcIdToContract(id: string, contractId: string): Promise<ICcIdResponse> {
    const response = await this.api.patch<ApiResponse<ICcIdResponse>>(`${this.basePath}/${id}/assign/${contractId}`);
    return this.extractData(response);
  }

  /**
   * Desassocia um ICCID de um contrato
   */
  async unassignICcIdFromContract(id: string): Promise<ICcIdResponse> {
    const response = await this.api.patch<ApiResponse<ICcIdResponse>>(`${this.basePath}/${id}/unassign`);
    return this.extractData(response);
  }

  // ===== MÉTODOS DE CONTROLE DE ESTOQUE =====

  /**
   * Retorna resumo do estoque de ICCIDs
   */
  async getStockSummary(): Promise<ICcIdStockSummaryResponse> {
    const response = await this.api.get<ApiResponse<ICcIdStockSummaryResponse>>(`${this.basePath}/stock/summary`);
    return this.extractData(response);
  }

  /**
   * Histórico append-only de movimentos FAST (admin).
   * Contrato alinhado à spec `fastchip-stock-whatsapp-zapi-ia` / plano T09:
   * `GET /icCids/{icCidControlId}/movements` — ordenação cronológica preferencialmente na API; o cliente reordena por `occurredAt` se necessário.
   */
  async getFastChipMovementHistory(icCidControlId: string): Promise<FastChipMovementEventDto[]> {
    const url = `${this.basePath}/${encodeURIComponent(icCidControlId)}/movements`;
    try {
      const response = await this.api.get<
        ApiResponse<FastChipMovementEventDto[] | FastChipMovementHistoryResponse>
      >(url);
      const raw = this.extractData(response);
      if (Array.isArray(raw)) {
        return raw;
      }
      if (raw && typeof raw === 'object') {
        const envelope = raw as FastChipMovementHistoryResponse;
        if (Array.isArray(envelope.items)) {
          return envelope.items;
        }
        if (Array.isArray(envelope.movements)) {
          return envelope.movements;
        }
      }
      return [];
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return [];
      }
      throw err;
    }
  }

  /**
   * Retorna estoque de ICCIDs por estado
   */
  async getStockByState(): Promise<ICcIdStockResponse[]> {
    const response = await this.api.get<ApiResponse<ICcIdStockResponse[]>>(`${this.basePath}/stock/by-state`);
    return this.extractData(response);
  }

  /**
   * Retorna disponibilidade de todos os ICCIDs
   */
  async getAvailability(): Promise<ICcIdAvailabilityResponse[]> {
    const response = await this.api.get<ApiResponse<ICcIdAvailabilityResponse[]>>(`${this.basePath}/availability`);
    return this.extractData(response);
  }

  /**
   * Flag de alocação atômica de ICCID no backend. Não toca na base de dados.
   * Substitui a sonda antiga via available-count (COUNT(*) só para ler um booleano).
   */
  async getAllocationFlag(signal?: AbortSignal): Promise<ICcIdAllocationFlagResponse> {
    const response = await this.api.get<ApiResponse<ICcIdAllocationFlagResponse> | ICcIdAllocationFlagResponse>(
      `${this.basePath}/allocation-flag`,
      { signal }
    );
    return this.extractData(response);
  }

  /**
   * Retorna a QUANTIDADE de ICCIDs Fastchip disponíveis para um cluster/estado.
   * Usado pelo modal de Novo Contrato para validar estoque em tempo real
   * (não retorna a lista). Pelo menos um entre `cluster` e `estado` deve ser informado.
   */
  async getAvailableCount(params: {
    cluster?: string | null;
    estado?: string | null;
    isEsim?: boolean | null;
    signal?: AbortSignal;
  }): Promise<ICcIdAvailableCountResponse> {
    const query = new URLSearchParams();
    if (params.cluster) query.append('cluster', params.cluster);
    if (params.estado) query.append('estado', params.estado);
    if (params.isEsim !== undefined && params.isEsim !== null) {
      query.append('isEsim', String(params.isEsim));
    }

    // O endpoint responde com o DTO direto (sem envelope ApiResponse); extractData
    // tolera ambos os formatos (com `success/data` ou objeto cru).
    const response = await this.api.get<ApiResponse<ICcIdAvailableCountResponse> | ICcIdAvailableCountResponse>(
      `${this.basePath}/available-count?${query.toString()}`,
      { signal: params.signal }
    );

    return this.extractData(response);
  }

  // ===== MÉTODOS DE OPERAÇÕES EM LOTE =====

  async makeAvailableBatch(ids: string[]): Promise<ICcIdBatchResultResponse> {
    const response = await this.api.patch<ApiResponse<ICcIdBatchResultResponse>>(
      `${this.basePath}/batch/make-available`,
      { ids } as ICcIdBatchActionRequest
    );
    return this.extractData(response);
  }

  async makeUnavailableBatch(ids: string[]): Promise<ICcIdBatchResultResponse> {
    const response = await this.api.patch<ApiResponse<ICcIdBatchResultResponse>>(
      `${this.basePath}/batch/make-unavailable`,
      { ids } as ICcIdBatchActionRequest
    );
    return this.extractData(response);
  }

  async softDeleteBatch(ids: string[]): Promise<ICcIdBatchResultResponse> {
    const response = await this.api.delete<ApiResponse<ICcIdBatchResultResponse>>(
      `${this.basePath}/batch/soft`,
      { data: { ids } as ICcIdBatchActionRequest }
    );
    return this.extractData(response);
  }

  /**
   * Edita em lote cadastro (UFs/cluster) de vários ICCIDs.
   * Modos: replace | add | remove | cluster-only. Auditoria por chip (movimento Updated com metadata do batchId).
   */
  async batchUpdate(request: BatchUpdateICcIdsRequest): Promise<BatchUpdateICcIdsResult> {
    const response = await this.api.post<ApiResponse<BatchUpdateICcIdsResult>>(
      `${this.basePath}/batch-update`,
      request
    );
    return this.extractData(response);
  }

  // ===== MÉTODOS DE VALIDAÇÃO =====

  /**
   * Verifica se um CCID já existe
   */
  async checkICcIdExists(ccId: string): Promise<boolean> {
    const response = await this.api.get<ApiResponse<boolean>>(`${this.basePath}/exists/${ccId}`);
    return this.extractData(response);
  }

  // ===== MÉTODOS DE UTILIDADE =====

  /**
   * Retorna lista de ICCIDs para seleção (formato simplificado)
   * Usa o endpoint de busca com filtros específicos
   */
  async getICcIdsForSelection(): Promise<ICcIdListResponse[]> {
    const response = await this.searchICcIds({}, 1, 1000);
    return response.items.map(item => ({
      id: item.id,
      ccId: item.ccId,
      estados: item.estados,
      disponivel: item.disponivel,
      contractNumber: item.contractNumber,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      deletedAt: item.deletedAt,
      updatedBy: item.updatedBy,
      deletedBy: item.deletedBy,
      isDeleted: item.isDeleted
    }));
  }

  /**
   * Retorna ICCIDs disponíveis para um estado específico
   * Usa o endpoint de busca com filtros específicos
   */
  async getAvailableICcIdsByState(estado: string): Promise<ICcIdListResponse[]> {
    const response = await this.searchICcIds({ estado, disponivel: true }, 1, 1000);
    return response.items.map(item => ({
      id: item.id,
      ccId: item.ccId,
      estados: item.estados,
      disponivel: item.disponivel,
      contractNumber: item.contractNumber,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      deletedAt: item.deletedAt,
      updatedBy: item.updatedBy,
      deletedBy: item.deletedBy,
      isDeleted: item.isDeleted
    }));
  }

  /**
   * Retorna estatísticas de uso de ICCIDs
   * Usa o endpoint de resumo do estoque
   */
  async getUsageStatistics(): Promise<{
    totalICcIds: number;
    availableICcIds: number;
    unavailableICcIds: number;
    assignedICcIds: number;
    unassignedICcIds: number;
    byState: { [estado: string]: number };
  }> {
    const summary = await this.getStockSummary();
    const byState: { [estado: string]: number } = {};
    
    summary.porEstado.forEach(item => {
      byState[item.estado] = item.total;
    });

    return {
      totalICcIds: summary.totalGeral,
      availableICcIds: summary.totalDisponivel,
      unavailableICcIds: summary.totalIndisponivel,
      assignedICcIds: summary.totalIndisponivel, // Assumindo que indisponível = atribuído
      unassignedICcIds: summary.totalDisponivel,
      byState
    };
  }
}

// Instância singleton
export const icCidService = new ICcIdService();
